import { StorySegment, UserProfile } from '@/types';

export type QuestStatus = 'active' | 'completed';

export interface Quest {
  id: string;
  userId: string;
  cartridgeId: string;
  title: string;
  description: string;
  progress: number;
  required: number;
  status: QuestStatus;
  createdAt: string;
  updatedAt: string;
}

class QuestService {
  private key(userId: string, cartridgeId: string) {
    return `quests_${userId}_${cartridgeId}`;
  }

  getActiveQuests(userId: string, cartridgeId: string): Quest[] {
    try {
      const raw = localStorage.getItem(this.key(userId, cartridgeId));
      if (!raw) return [];
      const quests: Quest[] = JSON.parse(raw) || [];
      return quests.filter(q => q.status === 'active');
    } catch {
      return [];
    }
  }

  save(userId: string, cartridgeId: string, quests: Quest[]) {
    try { localStorage.setItem(this.key(userId, cartridgeId), JSON.stringify(quests)); } catch {}
  }

  upsertQuest(userId: string, cartridgeId: string, quest: Quest) {
    const all = this.getAll(userId, cartridgeId);
    const idx = all.findIndex(q => q.id === quest.id);
    if (idx >= 0) all[idx] = quest; else all.push(quest);
    this.save(userId, cartridgeId, all);
  }

  getAll(userId: string, cartridgeId: string): Quest[] {
    try {
      const raw = localStorage.getItem(this.key(userId, cartridgeId));
      return raw ? (JSON.parse(raw) as Quest[]) : [];
    } catch {
      return [];
    }
  }

  markProgress(userId: string, cartridgeId: string, questId: string, delta = 1): Quest | null {
    const all = this.getAll(userId, cartridgeId);
    const q = all.find(x => x.id === questId);
    if (!q) return null;
    q.progress = Math.min(q.required, q.progress + delta);
    if (q.progress >= q.required) q.status = 'completed';
    q.updatedAt = new Date().toISOString();
    this.save(userId, cartridgeId, all);
    return q;
  }

  complete(userId: string, cartridgeId: string, questId: string): Quest | null {
    const all = this.getAll(userId, cartridgeId);
    const q = all.find(x => x.id === questId);
    if (!q) return null;
    q.status = 'completed';
    q.progress = q.required;
    q.updatedAt = new Date().toISOString();
    this.save(userId, cartridgeId, all);
    return q;
  }

  /**
   * Simple rules-based quest generation from a story segment.
   */
  generateFromStorySegment(userId: string, cartridgeId: string, segment: StorySegment): Quest[] {
    const text = `${segment.narrative} ${segment.choices?.join(' ')}`.toLowerCase();
    const all = this.getAll(userId, cartridgeId);
    const addQuest = (id: string, title: string, desc: string, required = 3) => {
      if (all.some(q => q.id === id)) return;
      const now = new Date().toISOString();
      all.push({ id, userId, cartridgeId, title, description: desc, progress: 0, required, status: 'active', createdAt: now, updatedAt: now });
    };

    if (text.includes('investigate') || text.includes('scan') || text.includes('analyze')) {
      addQuest('quest_investigation', 'Conduct Investigation', 'Gather clues and scan data nodes across the plaza.', 3);
    }
    if (text.includes('help') || text.includes('assist') || text.includes('protect')) {
      addQuest('quest_assistance', 'Assist Citizens', 'Provide aid to 3 civilians in need around Neo-Tokyo.', 3);
    }
    if (text.includes('hack') || text.includes('breach') || text.includes('terminal')) {
      addQuest('quest_hacknet', 'Breach Secure Node', 'Access 2 restricted terminals without detection.', 2);
    }

    this.save(userId, cartridgeId, all);
    return all.filter(q => q.status === 'active');
  }
}

export const questService = new QuestService();
export type { Quest };

